import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  collection,
  query,
  where,
  getDocs,
  getCountFromServer,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { Layout } from "../components/Layout";
import { PageHeader } from "../components/PageHeader";
import { StatusBadge, statusToTone } from "../components/StatusBadge";
import { formatDateLong, toDate } from "../lib/format";

interface Counts {
  activeServices: number;
  openRequests: number;
  activeProjects: number;
  remainingCredits: number;
}

export default function Dashboard() {
  // The client profile is shared via AuthContext — already fetched once at
  // login. Reading it here avoids a redundant getDoc(clients/:id) per page load.
  const { clientId, clientProfile: client } = useAuth();
  const [counts, setCounts] = useState<Counts | null>(null);

  useEffect(() => {
    if (!clientId) return;
    (async () => {
      try {
        // Counts use Firestore COUNT aggregation so we don't pull document
        // payloads just to call .size. All four queries fan out in parallel.
        const [
          servicesCount,
          requestsCount,
          projectsCount,
          creditsSnap,
        ] = await Promise.all([
          getCountFromServer(
            query(
              collection(db, "clientServices"),
              where("clientId", "==", clientId),
              where("status", "==", "active")
            )
          ),
          getCountFromServer(
            query(
              collection(db, "supportRequests"),
              where("clientId", "==", clientId),
              // Statuses live as lowercase snake_case in Firestore (see
              // functions/src/admin/support.ts).
              where("status", "in", [
                "submitted",
                "in_review",
                "waiting_on_client",
                "in_progress",
              ])
            )
          ),
          getCountFromServer(
            query(
              collection(db, "projects"),
              where("clientId", "==", clientId),
              where("status", "==", "active")
            )
          ),
          // Credits still need full docs because we sum the `remainingAmount`
          // field — no aggregation for that.
          getDocs(
            query(collection(db, "credits"), where("clientId", "==", clientId))
          ),
        ]);

        let remaining = 0;
        creditsSnap.forEach((d) => {
          remaining += (d.data().remainingAmount as number) ?? 0;
        });

        setCounts({
          activeServices: servicesCount.data().count,
          openRequests: requestsCount.data().count,
          activeProjects: projectsCount.data().count,
          remainingCredits: remaining,
        });
      } catch (err) {
        // Most often: indexes still building, or no documents yet.
        console.error(err);
        setCounts({
          activeServices: 0,
          openRequests: 0,
          activeProjects: 0,
          remainingCredits: 0,
        });
      }
    })();
  }, [clientId]);

  const loading = counts === null;

  return (
    <Layout>
      <PageHeader
        eyebrow={client?.companyName ?? "Account"}
        title={
          client?.contactName
            ? `Hello, ${client.contactName.split(" ")[0]}`
            : "Hello"
        }
        subtitle="A snapshot of everything happening across your ProNerdz account."
      />

      {/* Status row */}
      <div className="mb-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <StatRow
          label="Account"
          value={client?.accountStatus ?? "—"}
          tone={statusToTone(client?.accountStatus)}
        />
        <StatRow
          label="Billing"
          value={client?.billingStatus ?? "—"}
          tone={statusToTone(client?.billingStatus)}
        />
        <StatRow
          label="Next billing"
          value={
            client?.nextBillingDate
              ? formatDateLong(toDate(client.nextBillingDate))
              : "—"
          }
        />
      </div>

      {/* Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card
          title="Active Plan"
          big={client?.activePlan ?? "No subscription"}
          loading={!client}
          link={{ to: "/billing", label: "Manage billing →" }}
          delay={0}
        />
        <Card
          title="Billing Status"
          big={client?.billingStatus ?? "—"}
          loading={!client}
          link={{ to: "/billing", label: "View invoices →" }}
          delay={50}
          accent={
            client?.billingStatus === "past_due"
              ? "warning"
              : undefined
          }
        />
        <Card
          title="Active Services"
          big={counts ? counts.activeServices.toString() : ""}
          loading={loading}
          link={{ to: "/services", label: "View services →" }}
          delay={100}
        />
        <Card
          title="Open Requests"
          big={counts ? counts.openRequests.toString() : ""}
          loading={loading}
          link={{ to: "/support", label: "Submit request →" }}
          delay={150}
        />
        <Card
          title="Credits Remaining"
          big={counts ? counts.remainingCredits.toString() : ""}
          loading={loading}
          subtitle="across all packages"
          link={{ to: "/services", label: "View packages →" }}
          delay={200}
        />
        <Card
          title="Active Projects"
          big={counts ? counts.activeProjects.toString() : ""}
          loading={loading}
          link={{ to: "/projects", label: "View projects →" }}
          delay={250}
        />
      </div>

      {/* Past due alert */}
      {client?.billingStatus === "past_due" && (
        <div className="mt-8 rounded-xl border border-amber-300 bg-amber-50 p-5">
          <div className="font-medium text-amber-900">
            A recent payment didn't go through.
          </div>
          <p className="mt-1 text-sm text-amber-800">
            Update your payment method to keep your services active.
          </p>
          <Link to="/billing" className="btn-primary mt-3">
            Update payment method
          </Link>
        </div>
      )}
    </Layout>
  );
}

function StatRow({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: ReturnType<typeof statusToTone>;
}) {
  return (
    <div className="card flex items-center justify-between px-4 py-3">
      <span className="label">{label}</span>
      {tone ? <StatusBadge status={value} tone={tone} /> : <span className="text-sm font-medium">{value}</span>}
    </div>
  );
}

function Card({
  title,
  big,
  subtitle,
  link,
  delay,
  accent,
  loading,
}: {
  title: string;
  big: string;
  subtitle?: string;
  link?: { to: string; label: string };
  delay?: number;
  accent?: "warning";
  loading?: boolean;
}) {
  return (
    <div
      className={`card rise p-5 ${accent === "warning" ? "ring-1 ring-amber-300" : ""}`}
      style={{ animationDelay: `${delay ?? 0}ms` }}
    >
      <div className="label">{title}</div>
      {loading ? (
        // Skeleton bar — same height as the rendered text so the layout
        // doesn't shift when the value lands. Subtle pulse for affordance.
        <div className="mt-3 h-9 w-32 rounded-md bg-ink-100 animate-pulse" />
      ) : (
        <div className="mt-3 display text-3xl font-semibold capitalize">
          {big}
        </div>
      )}
      {subtitle && <div className="mt-1 text-xs text-ink-400">{subtitle}</div>}
      {link && (
        <Link
          to={link.to}
          className="mt-4 inline-block text-sm font-medium text-ink-900 hover:underline underline-offset-4"
        >
          {link.label}
        </Link>
      )}
    </div>
  );
}
